import type { GroupedOption, SingleValue } from '@ksyos/design-system';
import { DateTime } from '@ksyos/date-time';
import {
    Button,
    Card,
    Form,
    FormButtonRow,
    FormGroup,
    ProcessItemContainer,
    ProcessItemHeader,
    Select,
} from '@ksyos/design-system';
import type { OutpatientClinicTask } from '@common/contexts/support/domain/models/outpatientClinicTaskModel';
import type { OutpatientClinicTaskWithAuthorizationAndNotes } from '@common/contexts/support/domain/types/customOutpatientClinicTaskTypes';
import type {
    ModelAsDatabaseResource,
    ModelAsDto,
    ModelAsRepositoryResource,
} from '@common/contexts/support/domain/types/modelType';
import type { IEpisodeCategoryBasic } from '@common/interfaces';
import type { IProcessActorPractitioner } from '@common/interfaces/api/processActorsApi';
import type { IPatientTaskCustomControlData } from '@common/interfaces/patientTaskCustomControlData';
import type { IPerson } from '@common/utils/getDisplayName';
import type { ITimeSlots } from '@common/validation/timeSlots';
import type { ValidationErrors } from '@frontend/utils';
import { PatientTaskStatus, Profession } from '@common/enums';
import { getDisplayName } from '@common/utils/getDisplayName';
import {
    patientTaskAddSchema,
    patientTaskAddWithDescriptionSchema,
    patientTaskEditSchema,
    patientTaskEditWithDescriptionSchema,
} from '@common/validation/patientTask';
import {
    availabilityValidatorSchema,
    validateBothInputsFilled,
    validateTimePeriod,
    validateTimePeriodOverlap,
} from '@common/validation/timeSlots';
import { TimeRangesAddEdit } from '@frontend/components/elements/TimeRangesAddEdit';
import { TextAreaFormGroupField } from '@frontend/components/fields/TextAreaField';
import { translatePatientTaskType } from '@frontend/translate/custom';
import {
    emptyValidationErrors,
    getValidationErrorTranslation,
    updateValidationErrorsForMany,
    updateValidationErrorsForOne,
    validate,
} from '@frontend/utils';
import type { IEmployment } from '../../interfaces/employment';
import { AbortableFetch, fetchJson } from '../../../store/fetch';
import { getSelectedLanguage } from '../../../translate';
import { Component } from '../../elements/Component';
import { FormContextProvider } from '../../elements/formContext';
import { DateInput } from '../../fields/Date';
import { getTaskGroupsAndTypesForEpisodeCategory } from '../outpatientClinic/utils/outpatientClinicTaskTypesUtils';
import { PatientTaskItemPerformerSelect } from './PatientTaskItemPerformerSelect';

interface IPatientTaskItemAddEditProps {
    episodeCategory: IEpisodeCategoryBasic;
    onCancel: () => void;
    onSave: () => void;
    organizationId: string;
    patientId: string;
    task?: OutpatientClinicTaskWithAuthorizationAndNotes;
}

type GroupedOptionWithDescription = GroupedOption & {
    withDescription?: boolean;
};

interface IPatientTaskItemAddEditState {
    buttonDisabled?: boolean;
    employments: IEmployment[];
    errors: ValidationErrors;
    formId: string;
    performerPractitionerOptions: GroupedOption[];
    processActors: IProcessActorPractitioner[];
    task: Partial<ModelAsDto<ModelAsRepositoryResource<Omit<OutpatientClinicTask, 'updated_at' | 'patient'>>>> & {
        patientId: string;
    };
    typeOfTaskOptions: Map<string, GroupedOptionWithDescription>;
}

const emptyAvailability = {
    firstTimeRange: {
        endTime: null,
        startTime: null,
    },
    secondTimeRange: {
        endTime: null,
        startTime: null,
    },
};

export class PatientTaskItemAddEdit extends Component<IPatientTaskItemAddEditProps, IPatientTaskItemAddEditState> {
    private readonly abortableFetch = new AbortableFetch();
    private readonly abortController = new AbortController();
    private readonly translate = getSelectedLanguage();

    constructor(props: IPatientTaskItemAddEditProps) {
        super(props);

        this.state = {
            employments: [],
            errors: emptyValidationErrors,
            formId: 'processForm' + (props.task?.id ?? ''),
            performerPractitionerOptions: [],
            processActors: [],
            task: props.task
                ? {
                      ...props.task,
                      customControlData: props.task.customControlData ?? { availability: emptyAvailability },
                      dateCompleted: props.task.dateCompleted
                          ? DateTime.from(props.task.dateCompleted).format('YYYY-MM-DD')
                          : null,
                      episodeCategoryId: props.episodeCategory.id,
                      expectedDateCompleted: DateTime.from(props.task.expectedDateCompleted).format('YYYY-MM-DD'),
                      patientId: props.patientId,
                      performerOrganizationId: props.task.performerOrganization.id,
                      performerPractitionerId: props.task.performerPractitioner?.id ?? null,
                      performerPractitionerProfession: props.task.performerPractitionerProfession ?? null,
                      typeId: props.task.type.id ?? null,
                  }
                : {
                      customControlData: {
                          availability: emptyAvailability,
                      },
                      episodeCategoryId: props.episodeCategory.id,
                      expectedDateCompleted: DateTime.now().format('YYYY-MM-DD'),
                      patientId: props.patientId,
                      status: PatientTaskStatus.open,
                  },
            typeOfTaskOptions: new Map<string, GroupedOptionWithDescription>(),
        };
    }

    public async componentDidMount() {
        await this.initializeTypeOfTaskOptions();
        await this.loadPractitioners();
    }

    public componentWillUnmount() {
        this.abortableFetch.abort();
    }

    public render() {
        if (!this.state.task) {
            return null;
        }

        const saveButtonText = this.state.task.id
            ? this.translate.contactsAndTasks.saveTask
            : this.translate.contactsAndTasks.createTask;

        return (
            <Card>
                <Card.Transition type="edit" />
                <ProcessItemContainer>
                    <ProcessItemHeader
                        color="secondary"
                        date={this.state.task.dateCompleted}
                        icon="todo"
                        title={
                            (this.props.task?.description ?? this.state.task.id)
                                ? this.translate.contactsAndTasks.editTask
                                : this.translate.contactsAndTasks.newTask
                        }
                    />
                    <Form submitOnEnter={false}>
                        <FormContextProvider
                            value={{
                                errors: this.state.errors,
                                item: this.state.task,
                                onChange: async (key, value) => await this.handleChange(key, value),
                                onDoValidate: async (key) => await this.validateOne(key),
                                placeholderGroupName: 'task',
                            }}
                        >
                            <FormGroup
                                hasErrors={!this.state.task.description && this.state.errors.has('typeId')}
                                isRequired
                                label={this.translate.contactsAndTasks.task}
                            >
                                <Select
                                    error={
                                        !this.state.task.description
                                            ? getValidationErrorTranslation(this.state.errors.get('typeId'))
                                            : undefined
                                    }
                                    filterOption={this.filterTypeOfTaskOptions}
                                    key={'select-form-patient-task-option'}
                                    menuPlacement="bottom"
                                    onChange={async (selectedOption) => {
                                        await this.setState({
                                            task: {
                                                ...this.state.task,
                                                description: undefined,
                                                performerOrganizationId: undefined,
                                                performerPractitionerId: undefined,
                                                performerPractitionerProfession: undefined,
                                                typeId: selectedOption?.value,
                                            },
                                        });

                                        await this.validateOne('typeId');
                                    }}
                                    options={[...this.state.typeOfTaskOptions.values()]}
                                    placeholder="Kies een taak..."
                                    value={
                                        this.state.task.typeId
                                            ? this.state.typeOfTaskOptions.get(this.state.task.typeId)
                                            : null
                                    }
                                />
                            </FormGroup>

                            {this.state.task.typeId && this.checkPatientTaskHasDescription(this.state.task.typeId) && (
                                <TextAreaFormGroupField
                                    error={this.state.errors.get('description')}
                                    fieldKey="description"
                                    hasErrors={this.state.errors.has('description')}
                                    isRequired={true}
                                    label="Beschrijving"
                                    placeholder="Omschrijf de taak..."
                                    variant="outline"
                                />
                            )}

                            <FormGroup
                                hasErrors={
                                    this.state.errors.has('performerPractitionerProfession') ??
                                    this.state.errors.has('performerPractitionerId')
                                }
                                htmlFor="performer"
                                isRequired={true}
                                label={this.translate.contactsAndTasks.performer}
                            >
                                <PatientTaskItemPerformerSelect
                                    error={
                                        this.state.errors.get('performerPractitionerId') ??
                                        this.state.errors.get('performerPractitionerProfession')
                                    }
                                    id="performer"
                                    onChange={(selectedOption) => this.performerOnChange(selectedOption)}
                                    options={this.state.performerPractitionerOptions}
                                    value={
                                        this.state.performerPractitionerOptions.find(
                                            (item) => item.value === this.state.task.performerPractitionerId
                                        ) ??
                                        this.state.performerPractitionerOptions.find(
                                            (item) => item.value === this.state.task.performerPractitionerProfession
                                        )
                                    }
                                />
                            </FormGroup>
                            <FormGroup
                                hasErrors={this.state.errors.has('expectedDateCompleted')}
                                isRequired={true}
                                label={this.translate.contactsAndTasks.expectedDateCompleted}
                            >
                                <DateInput
                                    error={this.state.errors.get('expectedDateCompleted')}
                                    fieldKey="expectedDateCompleted"
                                    hasMaxWidth={true}
                                    placeholder={`${this.translate.common.date}...`}
                                    size="medium"
                                    variant="outline"
                                />
                            </FormGroup>
                            <TimeRangesAddEdit
                                error={'customControlData.availability'}
                                errors={this.state.errors}
                                onChange={(value, timeSlots) =>
                                    this.handleChangeCustomControlData(value, timeSlots, 'availability')
                                }
                                onDoValidate={() => this.validateCustomControlData(false)}
                                timeSlots={this.state.task.customControlData?.availability ?? emptyAvailability}
                            />
                        </FormContextProvider>
                        <FormButtonRow>
                            <Button
                                onClick={() => this.props.onCancel()}
                                variant="outline"
                            >
                                {this.translate.common.cancel}
                            </Button>
                            <Button
                                isDisabled={this.state.buttonDisabled}
                                onClick={async () => await this.savePatientTask()}
                                variant="solid"
                            >
                                {saveButtonText}
                            </Button>
                        </FormButtonRow>
                    </Form>
                </ProcessItemContainer>
            </Card>
        );
    }

    private async handleChangeCustomControlData(
        value: ITimeSlots,
        timeSlots: keyof ITimeSlots,
        field: keyof IPatientTaskCustomControlData
    ): Promise<void> {
        if (!this.state.task.customControlData) {
            return;
        }

        const customControlDataUpdate: IPatientTaskCustomControlData = {
            ...this.state.task.customControlData,
            [field]: {
                ...this.state.task.customControlData[field],
                [timeSlots]: value[timeSlots],
            },
        };
        await this.setState(({ task }) => ({ task: { ...task, ['customControlData']: customControlDataUpdate } }));
    }

    private async handleChange<T, K extends keyof T>(name: K, value: T[K]) {
        await this.setState(({ task }) => ({ task: { ...task, [name]: value } }));
    }

    private async validateCustomControlData(saving: boolean): Promise<void> {
        const customControlData = this.state.task.customControlData;
        const availability = customControlData?.availability ?? emptyAvailability;

        let validationErrors = this.state.errors;

        for (const value of Object.keys(availability)) {
            validationErrors = validationErrors
                .remove(`customControlData.availability.${value}.startTime`)
                .remove(`customControlData.availability.${value}.endTime`);
        }

        const firstTimeRangeFieldKey = `customControlData.availability.firstTimeRange.startTime`;
        const secondTimeRangeFieldKey = `customControlData.availability.secondTimeRange.startTime`;

        validationErrors = validateTimePeriod(availability.firstTimeRange, validationErrors, firstTimeRangeFieldKey);
        validationErrors = validateTimePeriod(availability.secondTimeRange, validationErrors, secondTimeRangeFieldKey);

        if (saving) {
            validationErrors = validateBothInputsFilled(
                availability.firstTimeRange,
                validationErrors,
                firstTimeRangeFieldKey
            );

            validationErrors = validateBothInputsFilled(
                availability.secondTimeRange,
                validationErrors,
                secondTimeRangeFieldKey
            );
        }

        if (!validationErrors.has(secondTimeRangeFieldKey)) {
            validationErrors = validateTimePeriodOverlap(availability, validationErrors, secondTimeRangeFieldKey);
        }

        const timeSchemaValidation = updateValidationErrorsForMany(
            availabilityValidatorSchema,
            this.state.task,
            validationErrors,
            [
                'customControlData.availability.firstTimeRange.startTime',
                'customControlData.availability.firstTimeRange.endTime',
                'customControlData.availability.secondTimeRange.startTime',
                'customControlData.availability.secondTimeRange.endTime',
            ]
        );

        const validationResult = timeSchemaValidation.merge(validationErrors);

        await this.setState({ errors: validationResult });
    }

    private async validateOne(name: string) {
        await this.setState(({ task, errors }) => ({
            errors: updateValidationErrorsForOne(this.getValidationSchema(), task, errors, name),
        }));
    }

    private async savePatientTask() {
        if (!this.state.task) {
            return;
        }

        await this.setState({ buttonDisabled: true, errors: emptyValidationErrors });

        const url = this.state.task.id ? `/api/patientTasks/${this.state.task.id}` : '/api/patientTasks';
        const patientTask = this.getPatientTaskToSave();
        const validationSchema = this.getValidationSchema();
        await this.validateCustomControlData(true);
        const schemaValidation = validate(validationSchema, patientTask);
        const validationResult = schemaValidation.errors.merge(this.state.errors);

        if (validationResult.size > 0) {
            await this.setState({ buttonDisabled: false, errors: validationResult });

            return;
        }

        await fetchJson(url, 'POST', { patientTask: schemaValidation.item });

        this.props.onSave();
    }

    private getPatientTaskToSave() {
        let patientTask: Partial<ModelAsDto<ModelAsDatabaseResource<Omit<OutpatientClinicTask, 'updated_at'>>>> = {};

        const description = this.checkPatientTaskHasDescription(this.state.task.typeId ?? '')
            ? this.state.task.description
            : null;

        if (this.state.task.id) {
            patientTask = {
                customControlData: this.state.task.customControlData,
                description,
                expectedDateCompleted: this.state.task.expectedDateCompleted,
                performerOrganizationId: this.state.task.performerOrganizationId,
                performerPractitionerId: this.state.task.performerPractitionerId,
                performerPractitionerProfession: this.state.task.performerPractitionerProfession,
                typeId: this.state.task.typeId ?? undefined,
            };
        } else {
            patientTask = {
                ...this.state.task,
                description,
            };
        }

        return patientTask;
    }

    private getValidationSchema() {
        const withDescription = this.checkPatientTaskHasDescription(this.state.task.typeId ?? '');

        if (withDescription) {
            return this.state.task.id ? patientTaskEditWithDescriptionSchema : patientTaskAddWithDescriptionSchema;
        }

        return this.state.task.id ? patientTaskEditSchema : patientTaskAddSchema;
    }

    private async loadPractitioners() {
        const result = await this.abortableFetch.fetchJson(
            `/api/employments/${this.props.organizationId}/practitioners?isActive=true&taskEpisodeCategory=${this.props.episodeCategory.id}`,
            'GET'
        );
        const employments = result.json.items;
        const performerPractitionerOptions = this.getEmploymentPerformerPractitionerOptions(employments);
        this.setState({ employments, performerPractitionerOptions });
    }

    private filterTypeOfTaskOptions(groupedOption: GroupedOption, input: string) {
        return groupedOption.label.toLowerCase().includes(input.toLowerCase()) || groupedOption.isGroupHeader === true;
    }

    private getEmploymentPerformerPractitionerOptions(employments: IEmployment[]) {
        const professions = [
            ...new Set<Profession | null>(employments.map((employment) => employment.profession)),
        ] as Profession[];
        const options: GroupedOption[] = [];

        for (const profession of professions) {
            options.push({
                isGroupHeader: true,
                label: this.translate.professionEnum[profession],
                value: profession,
            });
            const filteredServices = employments.filter((employment) => employment.profession === profession);
            filteredServices.forEach((employment) =>
                options.push({
                    isGroupOption: true,
                    label: getDisplayName(employment.practitioner as IPerson),
                    value: employment.practitioner?.id as string,
                })
            );
        }

        return options;
    }

    private async performerOnChange(selectedOption: SingleValue<GroupedOption>) {
        if (!selectedOption?.value) {
            return await this.setState({
                task: {
                    ...this.state.task,
                    performerOrganizationId: undefined,
                    performerPractitionerId: null,
                    performerPractitionerProfession: null,
                },
            });
        }

        if (Object.keys(Profession).includes(selectedOption.value)) {
            await this.setState({
                task: {
                    ...this.state.task,
                    performerOrganizationId: this.props.organizationId,
                    performerPractitionerId: null,
                    performerPractitionerProfession: selectedOption.value as Profession,
                },
            });
        } else {
            await this.setState({
                task: {
                    ...this.state.task,
                    performerOrganizationId: this.props.organizationId,
                    performerPractitionerId: selectedOption.value,
                    performerPractitionerProfession: this.getProfessionForPractitioner(
                        selectedOption.value
                    ) as Profession,
                },
            });
        }

        await this.validateOne('performerPractitionerProfession');
        await this.validateOne('performerPractitionerId');
    }

    private async initializeTypeOfTaskOptions() {
        const groupsAndTypes = await getTaskGroupsAndTypesForEpisodeCategory(
            {
                id: this.props.episodeCategory.id,
            },
            this.abortController.signal
        );

        const options: Map<string, GroupedOptionWithDescription> = new Map();
        const groups = groupsAndTypes?.groups ?? [];

        groups.forEach((group) => {
            options.set(`group.${group.name}`, {
                isDisabled: true,
                isGroupHeader: true,
                label: translatePatientTaskType(this.props.episodeCategory.name, group.name),
                value: `group.${group.name}`,
            });

            group.types.forEach((type) =>
                options.set(type.id, {
                    isGroupOption: true,
                    label: translatePatientTaskType(this.props.episodeCategory.name, type.name),
                    value: type.id,
                    withDescription: type.withDescription,
                })
            );
        });

        this.setState({ typeOfTaskOptions: options });
    }

    private getProfessionForPractitioner(practitionerId: string) {
        return this.state.employments.find((employment) => employment.practitioner?.id === practitionerId)?.profession;
    }

    private checkPatientTaskHasDescription(taskType: string) {
        return this.state.typeOfTaskOptions.get(taskType)?.withDescription ?? false;
    }
}
